import { presetsFor, runCommand } from '../../commands';
import { isCompactChrome } from '../../ui/compact';
import { isMprOpen } from '../../mpr';
import type { ViewerShell } from '../../shell';
import { isImageSeries } from '../../study';
import * as sync from '../../sync';
import type { AssistantAction, AssistantApi, FindingLocation } from './api';
import { conversationStudy, findSeries, resolveLocation } from './context';
import { aiLimitText } from './errors';
import { SEND_LABEL } from './radiologist/logic';
import { sendToReport } from './radiologist/send';
import { MAX_INSERT_TEXT, reportSection } from './radiologist/shapes';
import { reportSendMode } from './radiologist/switches';

/**
 * What an answer's buttons may do. Only the types in ACTIONS run, each through the shell API with
 * its arguments checked here (the server already re-validated them; the client checks again):
 * anything else the model or a future server sends is not rendered at all.
 */

export const ACTION_TEXT = {
  notInViewer: 'Esa imagen no está cargada en el visor.',
  priorMissing: 'El estudio previo no está cargado en el visor.',
  invalid: 'La acción sugerida no es válida.',
  synced: 'Sincronización de series activada.',
  algorithmFailed: 'No fue posible ejecutar el algoritmo.',
  algorithmNotApplicable: 'El algoritmo no aplica a esta serie.',
  algorithmDone: (n: number) => (n === 1 ? 'Algoritmo ejecutado: 1 hallazgo sugerido.' : `Algoritmo ejecutado: ${n} hallazgos sugeridos.`),
} as const;

const DEFAULT_LABELS: Record<string, string> = {
  go_to_slice: 'Ir al corte',
  open_series: 'Abrir serie',
  set_window: 'Aplicar ventana',
  open_prior: 'Abrir estudio previo',
  sync_prior: 'Sincronizar con el previo',
  set_layout: 'Cambiar distribución',
  open_mpr: 'Abrir MPR',
  run_algorithm: 'Ejecutar algoritmo',
  // insert_report: "Enviar al informe" or "Copiar al informe", by session (actionLabel).
};

const MAX_LAYOUT = 4;

export interface ActionContext {
  shell: ViewerShell;
  api: AssistantApi;
}

type Handler = (ctx: ActionContext, args: Record<string, unknown>) => void | Promise<void>;

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function int(value: unknown): number | null {
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

function finite(value: unknown): number | null {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

/** On the compact chrome the drawer covers the image: close it after a jump so the slice is visible. */
function revealImage(shell: ViewerShell): void {
  if (isCompactChrome()) shell.setRightDrawer(false);
}

/** Shows the slice a finding / action points at in the active viewport (same series: scroll only). */
export function jumpTo(shell: ViewerShell, location: FindingLocation): boolean {
  const target = resolveLocation(shell, location);
  if (!target) {
    shell.toast(ACTION_TEXT.notInViewer, 'warning');
    return false;
  }
  const vp = shell.activeViewport();
  const stack = vp?.currentStack;
  const same = stack && (stack.series === target.series
    || (stack.series.seriesInstanceUID === target.series.seriesInstanceUID && stack.series.instances.length === target.series.instances.length));
  if (vp && stack && same) {
    if (vp.currentIndex() !== target.index) window.cornerstoneTools.scrollToIndex(vp.element, target.index);
  } else {
    shell.guard(() => void shell.showSeriesIn(shell.activeIndex(), target.series, target.index));
  }
  revealImage(shell);
  return true;
}

function goToSlice({ shell }: ActionContext, args: Record<string, unknown>): void {
  const series = text(args.series_uid);
  if (!series) return void shell.toast(ACTION_TEXT.invalid, 'warning');
  jumpTo(shell, { series_uid: series, instance_uid: text(args.instance_uid), frame: Math.max(1, int(args.frame) ?? 1), index: int(args.index), box: null });
}

function openSeries({ shell }: ActionContext, args: Record<string, unknown>): void {
  const series = findSeries(shell, text(args.series_uid) ?? '');
  if (!series) return void shell.toast(ACTION_TEXT.notInViewer, 'warning');
  shell.guard(() => void shell.showSeriesIn(shell.activeIndex(), series));
  revealImage(shell);
}

function setWindow({ shell }: ActionContext, args: Record<string, unknown>): void {
  const preset = text(args.preset)?.toLowerCase();
  if (preset) {
    const index = presetsFor(shell).findIndex((p) => p.key.toLowerCase() === preset || p.label.toLowerCase() === preset);
    if (index < 0) return void shell.toast(ACTION_TEXT.invalid, 'warning');
    runCommand(`preset:${index + 1}`);
    return;
  }
  const width = finite(args.width);
  const center = finite(args.center);
  const vp = shell.activeViewport();
  const viewport = vp?.currentImage() ? window.cornerstone.getViewport(vp.element) : undefined;
  if (width === null || center === null || width <= 0 || !vp || !viewport) return void shell.toast(ACTION_TEXT.invalid, 'warning');
  viewport.voi = { windowWidth: width, windowCenter: center };
  window.cornerstone.setViewport(vp.element, viewport);
}

function openPrior({ shell }: ActionContext, args: Record<string, unknown>): void {
  const uid = text(args.study_uid);
  const study = shell.studies().find((s) => s.studyInstanceUID === uid);
  const series = study?.series.find(isImageSeries);
  if (!study || !series || uid === conversationStudy(shell)) return void shell.toast(ACTION_TEXT.priorMissing, 'warning');
  shell.guard(() => {
    if (shell.viewports().length < 2) shell.setLayout({ rows: 1, columns: 2 });
    const cell = (shell.activeIndex() + 1) % shell.viewports().length;
    void shell.showSeriesIn(cell, series);
  });
  revealImage(shell);
}

function syncPrior({ shell }: ActionContext): void {
  if (!sync.isLinked()) sync.setLink(true);
  shell.toast(ACTION_TEXT.synced);
}

function setLayout({ shell }: ActionContext, args: Record<string, unknown>): void {
  const rows = int(args.rows);
  const columns = int(args.columns);
  const ok = (n: number | null): n is number => n !== null && n >= 1 && n <= MAX_LAYOUT;
  if (!ok(rows) || !ok(columns)) return void shell.toast(ACTION_TEXT.invalid, 'warning');
  runCommand(`layout:${rows}x${columns}`);
}

function openMpr(): void {
  if (!isMprOpen()) runCommand('mpr');
}

async function runAlgorithm({ shell, api }: ActionContext, args: Record<string, unknown>): Promise<void> {
  const key = text(args.key);
  if (!key || !/^[a-z0-9-]{2,60}$/.test(key)) return void shell.toast(ACTION_TEXT.invalid, 'warning');
  const study = conversationStudy(shell);
  const series = shell.activeViewport()?.currentStack?.series;
  try {
    const run = await api.runAlgorithm(study, key, series?.studyInstanceUID === study ? series.seriesInstanceUID : null);
    if (run.outcome === 'not_applicable') shell.toast(ACTION_TEXT.algorithmNotApplicable, 'warning');
    else shell.toast(ACTION_TEXT.algorithmDone(run.findings));
  } catch (error) {
    console.warn('[viewer] assistant algorithm failed', error);
    shell.toast(aiLimitText(error) ?? ACTION_TEXT.algorithmFailed, 'error');
  }
}

/**
 * The exact text an insert_report action would send or copy, or null. The message shows this same string
 * under the button and the confirmation shows it in full: a model writes it, and text planted in an
 * image or a prior report must never reach the report unseen.
 */
export function reportText(action: AssistantAction): string | null {
  if (action.type !== 'insert_report') return null;
  const value = text(action.args.text);
  return value ? value.slice(0, MAX_INSERT_TEXT) : null;
}

/**
 * "Enviar al informe" (radiologist tools §2.1): appended to the reader's draft when the tool is on
 * and the ticket may write reports, else copied to the clipboard; either way only after the
 * radiologist confirmed it in a dialog that shows all of it. The section is the action's own when
 * it names a known one, else Hallazgos.
 */
async function insertReport({ shell, api }: ActionContext, args: Record<string, unknown>): Promise<void> {
  const value = reportText({ type: 'insert_report', label: '', args });
  if (!value) return void shell.toast(ACTION_TEXT.invalid, 'warning');
  await sendToReport(shell, api, [{ section: reportSection(args.section) ?? 'findings', text: value }]);
}

/** The closed map: action type → what the viewer does. */
const ACTIONS: Record<string, Handler> = {
  go_to_slice: goToSlice,
  open_series: openSeries,
  set_window: setWindow,
  open_prior: openPrior,
  sync_prior: syncPrior,
  set_layout: setLayout,
  open_mpr: openMpr,
  run_algorithm: runAlgorithm,
  insert_report: insertReport,
};

export function isSupportedAction(action: AssistantAction): boolean {
  return Object.prototype.hasOwnProperty.call(ACTIONS, action.type);
}

const REPORT_SECTIONS: Record<string, string> = { findings: 'hallazgos', impression: 'impresión', comparison: 'comparación' };
const ALGORITHM_KEY = /^[a-z0-9-]{2,60}$/;

/** What a button may add to its default label, from the action's own validated arguments only. */
function labelDetail(action: AssistantAction): string | null {
  const args = action.args;
  switch (action.type) {
    case 'go_to_slice': {
      const index = int(args.index);
      return index !== null && index >= 0 ? String(index + 1) : null;
    }
    case 'set_window': {
      const width = finite(args.width);
      const center = finite(args.center);
      return width !== null && center !== null ? `A ${Math.round(width)} · N ${Math.round(center)}` : null;
    }
    case 'set_layout': {
      const rows = int(args.rows);
      const columns = int(args.columns);
      return rows && columns && rows <= MAX_LAYOUT && columns <= MAX_LAYOUT ? `${rows}×${columns}` : null;
    }
    case 'run_algorithm': {
      const key = text(args.key);
      return key && ALGORITHM_KEY.test(key) ? key : null;
    }
    case 'insert_report':
      return REPORT_SECTIONS[text(args.section) ?? ''] ?? null;
    default:
      return null;
  }
}

/**
 * The button text. Built here from the type and its arguments, NEVER from the label the server sends:
 * that label is written by a model, and text planted in an image or a prior report could make a
 * harmless action read "Firmar informe".
 */
export function actionLabel(action: AssistantAction): string {
  const base = action.type === 'insert_report' ? SEND_LABEL[reportSendMode()] : DEFAULT_LABELS[action.type] ?? action.type;
  const detail = labelDetail(action);
  return detail ? `${base} ${detail}` : base;
}

export async function runAction(ctx: ActionContext, action: AssistantAction): Promise<void> {
  if (!isSupportedAction(action)) return;
  await ACTIONS[action.type](ctx, action.args);
}
